import apiClient from "./api-client";
import {
	ProjectEvaluation,
	FreelancerProfileEvaluationSummary,
	SubmitProjectEvaluationRequest,
	ModerateProjectEvaluationRequest,
	UpdateProjectEvaluationRequest,
	ProjectEvaluationSearchResult,
	ProjectEvaluationStatus,
	EvaluatorType
} from "../models/ProjectEvaluation";

/**
 * Get evaluations for a project
 * GET /api/project-evaluations/project/{projectId}
 */
export const getProjectEvaluations = async (
	projectId: number
): Promise<ProjectEvaluation[]> => {
	const response = await apiClient.get<ProjectEvaluation[]>(
		`/project-evaluations/project/${projectId}`
	);
	return response.data;
};

/**
 * Get published evaluations summary for a freelancer profile
 * GET /api/project-evaluations/freelancer-profile/{freelancerProfileId}
 */
export const getFreelancerProfileEvaluations = async (
	freelancerProfileId: number,
	evaluatorType?: EvaluatorType
): Promise<FreelancerProfileEvaluationSummary> => {
	const params = new URLSearchParams();
	if (evaluatorType) params.append("evaluatorType", evaluatorType);
	const queryString = params.toString();
	const response = await apiClient.get<FreelancerProfileEvaluationSummary>(
		`/project-evaluations/freelancer-profile/${freelancerProfileId}${queryString ? `?${queryString}` : ""}`
	);
	return response.data;
};

/**
 * Submit an evaluation (client or freelancer)
 * POST /api/project-evaluations/{id}/submit
 */
export const submitProjectEvaluation = async (
	id: number,
	request: SubmitProjectEvaluationRequest
): Promise<ProjectEvaluation> => {
	const response = await apiClient.post<ProjectEvaluation>(
		`/project-evaluations/${id}/submit`,
		request
	);
	return response.data;
};

/**
 * Moderate an evaluation (CSM only)
 * POST /api/project-evaluations/{id}/moderate
 */
export const moderateProjectEvaluation = async (
	id: number,
	request: ModerateProjectEvaluationRequest
): Promise<ProjectEvaluation> => {
	const response = await apiClient.post<ProjectEvaluation>(
		`/project-evaluations/${id}/moderate`,
		request
	);
	return response.data;
};

/**
 * Edit an evaluation (CSM only, when not published)
 * PATCH /api/project-evaluations/{id}/edit
 */
export const editProjectEvaluation = async (
	id: number,
	request: UpdateProjectEvaluationRequest
): Promise<ProjectEvaluation> => {
	const response = await apiClient.patch<ProjectEvaluation>(
		`/project-evaluations/${id}/edit`,
		request
	);
	return response.data;
};

/**
 * Search evaluations with filters (CSM only)
 * GET /api/project-evaluations/search
 */
export const searchProjectEvaluations = async (
	page: number = 1,
	pageSize: number = 20,
	status?: ProjectEvaluationStatus,
	evaluatorType?: EvaluatorType
): Promise<ProjectEvaluationSearchResult> => {
	const params = new URLSearchParams();
	params.append("page", page.toString());
	params.append("pageSize", pageSize.toString());
	if (status) params.append("status", status);
	if (evaluatorType) params.append("evaluatorType", evaluatorType);

	const response = await apiClient.get<ProjectEvaluationSearchResult>(
		`/project-evaluations/search?${params.toString()}`
	);
	return response.data;
};
